import { createColumnHelper } from '@tanstack/react-table';
import { X, CircleAlert, Check, RotateCw } from 'lucide-react';

import { getPlatformType } from '@/react/portainer/environments/utils';
import {
  AppliedPolicyStatusType,
  PlatformType,
} from '@/react/portainer/environments/types';
import {
  EnvironmentInfo,
  PolicyEnvironmentType,
  PolicyType,
} from '@/react/portainer/policies/types';

import { Badge } from '@@/Badge';
import { Link } from '@@/Link';
import { Button } from '@@/buttons';
import { buildExpandColumn } from '@@/datatables/expand-column';

import { getEnvironmentPolicyStatus } from '../../utils/isEnvironmentTypeSupportedByPolicy';

import { AttachmentRowData } from './types';

const groupColumnHelper = createColumnHelper<AttachmentRowData>();
const envColumnHelper = createColumnHelper<EnvironmentInfo>();

export function getGroupColumns(policyType?: PolicyType) {
  return [
    buildExpandColumn<AttachmentRowData>(),
    groupColumnHelper.accessor('Name', {
      header: 'Environment group',
      id: 'name',
      meta: { width: '250px' },
      cell: ({ getValue, row: { original: group } }) => (
        <Link
          to="portainer.groups.group"
          params={{ id: group.Id }}
          data-cy={`environment-group-link-${group.Id}`}
          className="truncate"
          title={getValue()}
        >
          {getValue()}
        </Link>
      ),
    }),
    groupColumnHelper.accessor('environments', {
      header: 'Status',
      id: 'status',
      meta: { width: '150px' },
      cell: ({ getValue }) => {
        if (!policyType) {
          return <span>Policy doesn&apos;t have an type set</span>;
        }
        const environments = getValue();
        const hasNonAppliedEnv = environments.some(
          (env) =>
            getEnvironmentPolicyStatus(env, policyType).Status !==
            AppliedPolicyStatusType.Applied
        );
        return (
          <Badge
            type={hasNonAppliedEnv ? 'warn' : 'success'}
            className="min-w-max"
          >
            {hasNonAppliedEnv ? (
              <CircleAlert className="h-3 w-3 mr-1" />
            ) : (
              <Check className="h-3 w-3 mr-1" />
            )}
            {hasNonAppliedEnv ? 'Warning' : 'Active'}
          </Badge>
        );
      },
    }),
    groupColumnHelper.accessor('environments', {
      header: 'Environments',
      id: 'environments-count',
      meta: { width: '350px' },
      cell: ({ getValue }) => {
        const environments = getValue();
        return (
          <span className="text-muted">
            {environments.length} environment
            {environments.length !== 1 ? 's' : ''}
          </span>
        );
      },
    }),
  ];
}

export function getEnvironmentColumns(
  policyType?: PolicyType,
  policyEnvType?: PolicyEnvironmentType
) {
  return [
    envColumnHelper.accessor('Name', {
      header: 'Environment name',
      id: 'name',
      meta: { width: '250px' },
      cell: ({ getValue, row: { original: env } }) => {
        const { to: linkTo, params: linkParams } = getEnvironmentLink(env);
        return (
          <Link
            to={linkTo}
            params={linkParams}
            data-cy={`environment-link-${env.Id}`}
            className="truncate"
            title={getValue()}
          >
            {getValue()}
          </Link>
        );
      },
    }),
    envColumnHelper.accessor('Status', {
      header: 'Status',
      id: 'status',
      meta: { width: '150px' },
      cell: ({ row: { original: env } }) => {
        if (!policyType) {
          return <span>Policy doesn&apos;t have an type set</span>;
        }

        const status = getEnvironmentPolicyStatus(env, policyType);

        if (status.Status === AppliedPolicyStatusType.NotSupported) {
          return (
            <div className="flex items-center gap-1">
              <Badge type="warn">
                <CircleAlert className="h-3 w-3 mr-1" />
                Warning
              </Badge>
            </div>
          );
        }
        if (status.Status === AppliedPolicyStatusType.InProgress) {
          return (
            <Badge type="warn">
              <CircleAlert className="h-3 w-3 mr-1" />
              Warning
            </Badge>
          );
        }
        if (status.Status === AppliedPolicyStatusType.Failed) {
          return (
            <Badge type="danger">
              <X className="h-3 w-3 mr-1" />
              Failed
            </Badge>
          );
        }

        if (status.Status === AppliedPolicyStatusType.Warning) {
          return <Badge type="warn">Warning</Badge>;
        }

        return (
          <Badge type="success">
            <Check className="h-3 w-3 mr-1" />
            Active
          </Badge>
        );
      },
    }),
    envColumnHelper.display({
      header: 'Message',
      id: 'message',
      meta: { width: '350px' },
      cell: ({ row }) => {
        if (!policyType) {
          return <span>Policy doesn&apos;t have an type set</span>;
        }
        if (!policyEnvType) {
          return <span>Policy doesn&apos;t have an environment type set</span>;
        }

        const status = getEnvironmentPolicyStatus(row.original, policyType);

        if (status.Status === AppliedPolicyStatusType.InProgress) {
          return (
            <span>
              The policy is currently being applied to this environment
            </span>
          );
        }

        return <span>{status.Message || '-'}</span>;
      },
    }),
    envColumnHelper.display({
      header: 'Action',
      id: 'action',
      meta: { width: '120px' },
      cell: ({ row }) => (
        <Button
          icon={RotateCw}
          title="Retry policy distribution"
          disabled
          color="default"
          data-cy={`retry-policy-button-${row.original.Id}`}
        >
          Retry
        </Button>
      ),
    }),
  ];
}

function getEnvironmentLink(env: EnvironmentInfo) {
  const platform = getPlatformType(env.Type);
  if (platform === PlatformType.Kubernetes) {
    return {
      to: 'kubernetes.dashboard',
      params: { endpointId: env.Id },
    };
  }

  return {
    to: 'docker.dashboard',
    params: { endpointId: env.Id },
  };
}
